// The frame-time harness the client reports from (R431, R505).
//
// The plan that produced it (Part C.7) had it measure two renderers with the
// same code, so it imports nothing from `fast/`: it takes a camera it can
// drive, four read-back functions, and a name for the backend it is timing.
// One client is left, and the harness stays engine-agnostic, because a
// measurement that knows the engine measures the engine's opinion of itself.
//
// What it costs while running: one `performance.now()` per phase call and
// one typed-array write. Every buffer is allocated once, so a measured
// frame allocates nothing and the garbage collector does not appear in the
// numbers it reports.
//
// Nothing here runs unless the page asks for it. `fast/main.ts` builds the
// harness behind `?perf=1` and passes `undefined` otherwise, and every call
// site is an optional call.

// The grid this world runs on. `world.ts` is engine-agnostic and the client
// mirrors the server's topology through it.
import { COLS, ROWS } from '../world';
import { writeStore } from '../ui/localstore';
import {
  type CameraTarget, type Scenario, type ScenarioCtx, type ScenarioId,
  scenariosOf,
} from './scenarios';

/**
 * How many frames of history the rings hold.
 *
 * 4096 frames is 68 seconds at 60 fps, so one path of the standard run fits
 * whole and no sample of it is ever overwritten. A longer run wraps, which
 * costs the oldest frames and never a report.
 */
export const RING_FRAMES = 4096;

/**
 * What counts as a long frame, in ms.
 *
 * 16.7 ms is one frame of a 60 Hz display. A frame over it missed its
 * present, which a player sees as a stutter however good the median is.
 */
export const LONG_FRAME_MS = 16.7;

/** The camera the harness drives. Both clients already have these two. */
export interface PerfCamera {
  centerOn(x: number, y: number, zoom?: number): void;
  scale(): number;
}

/** What the harness reads back from the client it is timing. */
export interface PerfDeps {
  camera: PerfCamera;
  /** The world rectangle in px. */
  world: () => { w: number; h: number };
  /** The player's own base, where a held path parks the camera. */
  home: () => { x: number; y: number };
  /** The owner byte of every cell, or null before the hello. */
  owners: () => Uint8Array | null;
  /** How many commanders this world holds. */
  population: () => number;
  /** How many fleets the client knows, and how many it draws. */
  fleets: () => { total: number; inView: number };
  /** What the renderer is, for the report header. */
  backend: string;
  /** Anything else worth recording per frame: draw calls, instances. */
  counters: () => Record<string, number>;
  /**
   * The last GPU figure the client's own timer completed, in ms, or null
   * where the backend gives none (R431).
   *
   * A timer query lands a few frames after the frame it timed, so the
   * harness samples this once a frame and pushes a value only where it
   * changed. What the column reports is therefore a spread over the
   * measurements that completed, not one figure per frame.
   */
  gpuMs?: () => number | null;
}

/** The spread of one measured quantity, in ms. */
export interface PerfSpread {
  p50: number;
  p95: number;
  p99: number;
  max: number;
  mean: number;
}

/** What one phase of the frame cost. */
export interface PerfPhase {
  name: string;
  meanMs: number;
  p95Ms: number;
  frames: number;
}

/**
 * One path's result.
 *
 * **`frameMs` is CPU time and nothing else** (R431, the measurement
 * correction). It is the wall time from `frameStart` to `frameEnd`, which is
 * the client's own work inside one animation-frame callback, and it stops at
 * the last draw call the frame submits. A WebGL draw call returns as soon as
 * the driver has taken the command, so the GPU's own work is NOT in it. A
 * client can therefore read 0.5 ms of `frameMs` and still present 20 frames
 * a second, and the first M5 table read exactly that way.
 *
 * **`intervalMs` is what the player sees.** It is the wall time from one
 * animation-frame callback to the next, and an animation frame is driven by
 * the compositor, so its median is one presented frame. `fps` is the same
 * measure counted rather than timed. Read the two side by side: `frameMs`
 * says what the client costs, `intervalMs` says what it delivers.
 */
export interface PerfRun {
  scenario: string;
  seconds: number;
  frames: number;
  fps: number;
  zoom: number;
  /** CPU ms inside the frame callback. Not the presented frame time. */
  frameMs: PerfSpread;
  /** Frames whose CPU work passed `LONG_FRAME_MS`. */
  longFrames: number;
  /** Wall ms between one animation frame and the next. */
  intervalMs: PerfSpread;
  /** Animation frames that arrived more than `LONG_FRAME_MS` after the last. */
  longIntervals: number;
  phases: PerfPhase[];
  counters: Record<string, number>;
  /**
   * GPU ms a frame, or null where the backend has no timer.
   *
   * Read it beside `frameMs`, which is CPU only: a client can submit a frame
   * in 0.3 ms of CPU and still hold the GPU for 9 ms.
   */
  gpuMs: PerfSpread | null;
}

/** What the run was measured on. A report with no header is not comparable. */
export interface PerfHeader {
  page: string;
  backend: string;
  dpr: number;
  window: { w: number; h: number };
  hidden: boolean;
  population: number;
  fleets: { total: number; inView: number };
  startedAt: string;
}

/** The whole report, as it is logged and stored. */
export interface PerfReport {
  header: PerfHeader;
  runs: PerfRun[];
}

/**
 * The rank-based percentile of a sorted sample.
 *
 * Nearest rank, not an interpolation: every value it answers is a frame
 * that really happened, which is what a frame-time report has to state.
 * `q` is a fraction from 0 to 1, and an empty sample answers 0.
 */
export function percentile(sorted: ArrayLike<number>, len: number, q: number): number {
  if (len <= 0) return 0;
  let rank = Math.ceil(q * len) - 1;
  if (rank < 0) rank = 0;
  if (rank >= len) rank = len - 1;
  return sorted[rank];
}

/**
 * The spread of one sample. The input is sorted in place, so pass a copy of
 * anything the caller still needs in frame order.
 */
export function spread(values: Float64Array, len: number): PerfSpread {
  if (len <= 0) return { p50: 0, p95: 0, p99: 0, max: 0, mean: 0 };
  const use = values.subarray(0, len);
  let total = 0;
  for (let i = 0; i < len; i++) total += use[i];
  use.sort();
  return {
    p50: percentile(use, len, 0.5),
    p95: percentile(use, len, 0.95),
    p99: percentile(use, len, 0.99),
    max: use[len - 1],
    mean: total / len,
  };
}

/** A fixed-size ring of frame samples. It never allocates after the first. */
class Ring {
  readonly buf: Float64Array;
  private at = 0;
  private written = 0;

  constructor(cap: number) {
    this.buf = new Float64Array(cap);
  }

  push(v: number) {
    this.buf[this.at] = v;
    this.at = (this.at + 1) % this.buf.length;
    this.written++;
  }

  /** How many samples the ring holds, which caps at its size. */
  get length(): number {
    return Math.min(this.written, this.buf.length);
  }

  reset() {
    this.at = 0;
    this.written = 0;
  }

  /** Copies the held samples into `out`, in no particular order. */
  copyInto(out: Float64Array): number {
    const len = this.length;
    for (let i = 0; i < len; i++) out[i] = this.buf[i];
    return len;
  }
}

/**
 * The number of wall seconds one path runs, from `?perfsec=N`.
 *
 * The default is the path's own 30 s. A smoke run of the harness itself
 * takes seconds, and the operator states that on the URL rather than in the
 * code.
 */
function secondsOverride(): number | null {
  const raw = new URLSearchParams(location.search).get('perfsec');
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export class PerfHarness {
  private readonly frames = new Ring(RING_FRAMES);
  private readonly intervals = new Ring(RING_FRAMES);
  private readonly gpu = new Ring(RING_FRAMES);
  private lastGpu: number | null = null;
  private readonly phases = new Map<string, Ring>();
  private readonly scratch = new Float64Array(RING_FRAMES);
  private frameAt = 0;
  private phaseAt = 0;
  private runs: PerfRun[] = [];
  private header: PerfHeader | null = null;
  private running = false;
  private cancelled = false;

  constructor(private readonly deps: PerfDeps) {}

  /** The top of the frame. Every phase after it is measured from here. */
  frameStart() {
    this.frameAt = performance.now();
    this.phaseAt = this.frameAt;
  }

  /**
   * The end of one named phase. The name is the work that just finished, so
   * a call site reads as the order the frame is drawn in.
   */
  phase(name: string) {
    const now = performance.now();
    let ring = this.phases.get(name);
    if (ring === undefined) {
      ring = new Ring(RING_FRAMES);
      this.phases.set(name, ring);
    }
    ring.push(now - this.phaseAt);
    this.phaseAt = now;
  }

  /** The bottom of the frame, after the renderer has submitted its work. */
  frameEnd() {
    if (this.frameAt === 0) return;
    this.frames.push(performance.now() - this.frameAt);
    // The GPU figure completes a few frames after the frame it timed, so the
    // same value is read many times. Push it once, or the spread reports the
    // sampling rate instead of the work (R431).
    const gpu = this.deps.gpuMs?.() ?? null;
    if (gpu !== null && gpu !== this.lastGpu) this.gpu.push(gpu);
    this.lastGpu = gpu;
  }

  /** Runs one path, or the four of `standard`, and answers the report. */
  async run(id: ScenarioId = 'standard'): Promise<PerfReport> {
    if (this.running) throw new Error('a perf run is already going');
    this.running = true;
    this.cancelled = false;
    this.runs = [];
    this.header = this.makeHeader();
    try {
      for (const scenario of scenariosOf(id)) {
        if (this.cancelled) break;
        this.runs.push(await this.runOne(scenario));
      }
    } finally {
      this.running = false;
    }
    return this.report();
  }

  /** The last report, or one built from whatever the rings hold now. */
  report(): PerfReport {
    return { header: this.header ?? this.makeHeader(), runs: this.runs.slice() };
  }

  /**
   * Stops a run in flight. The frames it already took are still reported.
   *
   * The flag is the whole stop, and the next animation frame reads it and
   * ends the path. The first draft also cancelled that animation frame, and
   * the frame it cancelled was the one that settles the path's promise: a
   * stopped run therefore never finished, `run()` never returned, and every
   * later call answered "a perf run is already going" (R431, found while
   * the M5 matrix was measured). Never cancel the callback that resolves.
   */
  stop() {
    this.cancelled = true;
  }

  private makeHeader(): PerfHeader {
    const fleets = this.deps.fleets();
    return {
      page: location.pathname + location.search,
      backend: this.deps.backend,
      dpr: devicePixelRatio,
      window: { w: window.innerWidth, h: window.innerHeight },
      // A hidden tab presents no frame (R304), so a report taken in one is
      // rejected. The harness records the flag rather than refusing to run:
      // the operator must see why the numbers are wrong.
      hidden: document.hidden,
      population: this.deps.population(),
      fleets: { total: fleets.total, inView: fleets.inView },
      startedAt: new Date().toISOString(),
    };
  }

  private ctx(): ScenarioCtx {
    return {
      world: this.deps.world(),
      home: this.deps.home(),
      owners: this.deps.owners(),
      cols: COLS,
      rows: ROWS,
    };
  }

  /** One ring's count, spread and how many samples passed `LONG_FRAME_MS`. */
  private take(ring: Ring): [number, PerfSpread, number] {
    const len = ring.copyInto(this.scratch);
    let long = 0;
    for (let i = 0; i < len; i++) if (this.scratch[i] > LONG_FRAME_MS) long++;
    return [len, spread(this.scratch, len), long];
  }

  /** Flies one path and measures the frames it produced. */
  private async runOne(scenario: Scenario): Promise<PerfRun> {
    const seconds = secondsOverride() ?? scenario.seconds;
    this.frames.reset();
    this.intervals.reset();
    this.gpu.reset();
    this.lastGpu = null;
    for (const ring of this.phases.values()) ring.reset();
    const ctx = this.ctx();
    let frame = 0;
    const startedAt = performance.now();
    // The gap to the last animation frame is the presented frame time: the
    // callback is driven by the compositor, so its cadence is the cadence
    // the display shows (R431). The first callback has no gap to measure.
    let lastRaf = 0;
    let last: CameraTarget = scenario.at(ctx, 0, 0);
    this.deps.camera.centerOn(last.x, last.y, last.zoom);
    await new Promise<void>((resolve) => {
      const step = () => {
        const now = performance.now();
        if (lastRaf !== 0) this.intervals.push(now - lastRaf);
        lastRaf = now;
        const elapsed = (now - startedAt) / 1000;
        if (this.cancelled || elapsed >= seconds) {
          resolve();
          return;
        }
        frame++;
        last = scenario.at(ctx, elapsed / seconds, frame);
        this.deps.camera.centerOn(last.x, last.y, last.zoom);
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
    const took = (performance.now() - startedAt) / 1000;
    const [len, frameMs, long] = this.take(this.frames);
    const [, intervalMs, longGaps] = this.take(this.intervals);
    const gpuLen = this.gpu.copyInto(this.scratch);
    const gpuMs = gpuLen > 0 ? spread(this.scratch, gpuLen) : null;
    const phases: PerfPhase[] = [];
    for (const [name, ring] of this.phases) {
      const n = ring.copyInto(this.scratch);
      if (n === 0) continue;
      const s = spread(this.scratch, n);
      phases.push({ name, meanMs: s.mean, p95Ms: s.p95, frames: n });
    }
    return {
      scenario: scenario.id,
      seconds: took,
      frames: len,
      fps: took > 0 ? len / took : 0,
      zoom: this.deps.camera.scale(),
      frameMs,
      longFrames: long,
      intervalMs,
      longIntervals: longGaps,
      phases,
      gpuMs,
      // Sampled once, at the end of the path: a per-frame read would
      // allocate an object every frame and appear in its own numbers.
      counters: { ...this.deps.counters() },
    };
  }
}

/** Where a finished report is left for the operator to read back. */
export const PERF_STORAGE_KEY = 'aquaphobia.perf.last';

/**
 * Puts the harness on `window.__perf` and returns it.
 *
 * The three methods are what a browser session drives from the console or
 * through claude-in-chrome: `await __perf.run('standard')`, then
 * `__perf.report()`. `stop()` ends a run that is taking too long.
 */
export function installPerf(deps: PerfDeps): PerfHarness {
  const perf = new PerfHarness(deps);
  (window as unknown as { __perf?: unknown }).__perf = {
    run: (id?: ScenarioId) => perf.run(id),
    report: () => perf.report(),
    stop: () => perf.stop(),
  };
  return perf;
}

/** Logs a report and leaves it in `localStorage` for the operator. */
export function publishReport(report: PerfReport) {
  const json = JSON.stringify(report, null, 2);
  console.log(json);
  // A private window refuses the write; the console line is the report.
  writeStore(PERF_STORAGE_KEY, json);
}
